/**
 * hero-video.js
 *
 * A hero background can supply a differently-framed clip per breakpoint, so the
 * section renders up to two <video> elements (desktop + mobile). Both live in
 * the DOM at once, and `display:none` does NOT stop a browser from downloading a
 * video — with `autoplay` it eagerly fetches and decodes BOTH clips on every
 * device, doubling the transfer.
 *
 * The section renders the videos inert instead: no `autoplay` and
 * `preload="none"`, so nothing downloads on paint. This module plays only the
 * clip whose `data-hero-video` matches the active breakpoint; the off-breakpoint
 * clip is never started, so its source is never fetched. Resizing across the
 * breakpoint swaps which clip plays (and only then downloads the second).
 *
 * Decorative enhancement only — with JS disabled, no hero video plays.
 */

import { prefersReducedMotion } from '@stardust/utilities';

const DESKTOP_QUERY = '(min-width: 768px)'; // Tailwind `md`

/**
 * Play the breakpoint-matching video inside a single hero section and keep the
 * others paused.
 * @param {HTMLElement} root - a `[data-hero]` section root
 */
function activate(root) {
  // Guard against stacking listeners: the initial init() and any later
  // shopify:section:load re-init can target the same root.
  if (root.dataset.heroVideoInit === 'true') return;

  const wrapperEls = root.querySelectorAll('[data-hero-video]');
  if (!wrapperEls.length) return;

  const wrappers = Array.from(wrapperEls)
    .map((wrapper) => ({
      target: wrapper.dataset.heroVideo, // 'desktop' | 'mobile' | 'all'
      video: wrapper.querySelector('video'),
    }))
    .filter(({ video }) => video);
  if (!wrappers.length) return;

  root.dataset.heroVideoInit = 'true';
  const desktopMql = window.matchMedia(DESKTOP_QUERY);
  // Everyone gets the pause control (WCAG 2.2.2); reduced motion only seeds
  // the initial paused state.
  let userPaused = prefersReducedMotion();
  const toggle = root.querySelector('[data-hero-video-toggle]');
  const pauseIcon = toggle?.querySelector('[data-hero-video-icon-pause]');
  const playIcon = toggle?.querySelector('[data-hero-video-icon-play]');

  const apply = () => {
    wrappers.forEach(({ target, video }) => {
      const matches =
        target === 'all' ||
        (target === 'desktop' && desktopMql.matches) ||
        (target === 'mobile' && !desktopMql.matches);

      if (matches && !userPaused) {
        // Required for muted inline autoplay on iOS Safari when triggered via JS.
        video.muted = true;
        video.setAttribute('playsinline', '');
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  };

  const syncToggle = () => {
    if (!toggle) return;
    toggle.setAttribute('aria-label', userPaused ? 'Play background video' : 'Pause background video');
    pauseIcon?.classList.toggle('hidden', userPaused);
    playIcon?.classList.toggle('hidden', !userPaused);
  };

  toggle?.addEventListener('click', () => {
    userPaused = !userPaused;
    apply();
    syncToggle();
  });

  syncToggle();
  apply();
  desktopMql.addEventListener('change', apply);
}

/**
 * Initialize all hero videos within a scope.
 * @param {ParentNode} [scope=document]
 */
export function initHeroVideo(scope = document) {
  scope.querySelectorAll('[data-hero]').forEach(activate);
}

initHeroVideo();

// Re-initialize when a hero section is (re)rendered in the theme editor.
document.addEventListener('shopify:section:load', (event) => {
  initHeroVideo(event.target);
});
